import { OTPInput, OTPInputContext } from 'input-otp';
import { Dot } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { cn } from '../utils/tailwind-merge';

const InputOTP = React.forwardRef<React.ElementRef<typeof OTPInput>, React.ComponentPropsWithoutRef<typeof OTPInput>>(
  ({ className, containerClassName, ...props }, ref) => {
    const inputRef = useRef<React.ElementRef<typeof OTPInput> | null>(null);

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    return (
      <OTPInput
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<typeof node | null>).current = node;
          }
        }}
        containerClassName={cn('flex items-center justify-center gap-2 has-[:disabled]:opacity-50', containerClassName)}
        className={cn('disabled:cursor-not-allowed', className)}
        {...props}
      />
    );
  },
);
InputOTP.displayName = 'InputOTP';

const InputOTPGroup = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('flex items-center', className)} {...props} />,
);
InputOTPGroup.displayName = 'InputOTPGroup';

const InputOTPSlot = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const slot = inputOTPContext?.slots?.[index];
  const char = slot?.char ?? '';
  const hasFakeCaret = slot?.hasFakeCaret ?? false;
  const isActive = slot?.isActive ?? false;
  return (
    <div
      ref={ref}
      className={cn(
        'relative flex size-10 items-center justify-center border-input text-sm ring-1 ring-neutral-5 transition-all first:rounded-l-md last:rounded-r-md',
        isActive && 'z-10 ring-2 ring-accent-9 ring-ring ring-offset-background',
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-black" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = 'InputOTPSlot';

const InputOTPSeparator = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ ...props }, ref) => (
    // biome-ignore lint/a11y/useFocusableInteractive: <explanation>
    <div ref={ref} role="separator" {...props}>
      <Dot />
    </div>
  ),
);
InputOTPSeparator.displayName = 'InputOTPSeparator';

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
